import { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { DataGrid, GridToolbar, useGridApiRef } from '@mui/x-data-grid';
import {
    Box, Alert, Button, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';

import Api from '../../Services/Api';
import { PAGE_SIZE, pageSizeOptions } from '../../Config/constants.js';
import { useTheme } from '@emotion/react';
import ActionButton from '../ActionButton.js';
import CustomerPdfForm from './CustomerPdfForm.js';

const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    const p = n => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const STATUS_LABELS = {
    word_generated:    { label: 'Word generato',      color: 'warning' },
    pdf_uploaded:      { label: 'PDF caricato',       color: 'info' },
    documento_inviato: { label: 'Documento inviato',  color: 'success' },
};

function CustomerPdfOverview({ customerId }) {
    const apiRef = useGridApiRef();
    const [rows, setRows] = useState([]);
    const [displayAlert, setDisplayAlert] = useState(null);
    const [formOpen, setFormOpen] = useState(false);

    // Upload dialog state
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadTargetId, setUploadTargetId] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);

    useEffect(() => {
        // StrictMode in dev monta due volte: entrambe le chiamate API partono,
        // ma solo la prima che risponde aggiorna lo stato; il flag evita setState stale.
        let ignore = false;
        Api.get({ endpoint: `document/${customerId}/list`, contentType: 'application/json' })
            .then(response => { if (!ignore) setRows(response.data.list); })
            .catch(error => {
                if (!ignore) {
                    console.log(JSON.stringify(error));
                    setDisplayAlert('error');
                }
            });
        return () => { ignore = true; };
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFormSuccess = (newSentPdf) => {
        setFormOpen(false);
        setRows(prev => [newSentPdf, ...prev]);
    };

    const openUpload = (id) => {
        setUploadTargetId(id);
        setUploadFile(null);
        setUploadError(null);
        setUploadDialogOpen(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.type !== 'application/pdf') {
            setUploadError('Il file deve essere in formato PDF.');
            setUploadFile(null);
            return;
        }
        setUploadError(null);
        setUploadFile(file);
    };

    const handleUploadSubmit = () => {
        if (!uploadFile) { setUploadError('Selezionare un file PDF.'); return; }
        setUploadLoading(true);

        const formData = new FormData();
        formData.append('pdf_file', uploadFile);

        Api.post({ endpoint: `document/${uploadTargetId}/upload-pdf`, body: formData }, true)
            .then(response => {
                const updated = response.data.sent_pdf;
                setRows(prev => prev.map(r => r.id === updated.id ? { ...r, status: updated.status } : r));
                setUploadDialogOpen(false);
                setDisplayAlert('uploaded');
            })
            .catch(() => setUploadError('Errore durante il caricamento. Riprovare.'))
            .finally(() => setUploadLoading(false));
    };

    const columns = [
        {
            field: 'code',
            headerName: 'Codice',
            flex: 1,
        },
        {
            field: 'template_name',
            headerName: 'Template',
            flex: 2,
        },
        {
            field: 'recipient_phone',
            headerName: 'Cellulare destinatario',
            flex: 2,
        },
        {
            field: 'status',
            headerName: 'Stato',
            flex: 1,
            renderCell: (params) => {
                const s = STATUS_LABELS[params.value] ?? { label: params.value, color: 'default' };
                return <Chip label={s.label} color={s.color} size="small" />;
            },
        },
        {
            field: 'sent_at',
            headerName: 'Data invio',
            flex: 1,
            type: 'dateTime',
            valueGetter: (params) => params.row.sent_at ? new Date(params.row.sent_at) : null,
            valueFormatter: (params) => params.value ? formatDate(params.value) : '',
        },
        {
            field: 'read_at',
            headerName: 'Data lettura',
            flex: 1,
            type: 'dateTime',
            valueGetter: (params) => params.row.read_at ? new Date(params.row.read_at) : null,
            valueFormatter: (params) => params.value ? formatDate(params.value) : '',
        },
        {
            field: 'id',
            headerName: 'Azioni',
            flex: 1,
            renderCell: (params) => {
                const onDownload = (e) => {
                    e.stopPropagation();
                    Api.get({ endpoint: `document/download-docx/${params.row.id}`, responseType: 'blob' })
                        .then(response => {
                            // Content-Disposition è leggibile solo se il server lo espone in Access-Control-Expose-Headers
                            const disposition = response.headers['content-disposition'] ?? '';
                            const match = disposition.match(/filename[^;=\n]*=['"]?([^'"\n;]+)['"]?/i);
                            const fileName = match ? match[1] : `${params.row.template_name}_${params.row.id}.docx`;
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', fileName);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                        })
                        .catch(() => setDisplayAlert('error'));
                };

                const onDelete = (e) => {
                    e.stopPropagation();
                    if (!window.confirm('Eliminare il record?')) return;
                    Api.del({ endpoint: `document/${params.row.id}`, contentType: 'application/json' })
                        .then(() => setRows(prev => prev.filter(r => r.id !== params.row.id)))
                        .catch(() => setDisplayAlert('error'));
                };

                const onDownloadPdf = (e) => {
                    e.stopPropagation();
                    Api.get({ endpoint: `document/download-pdf/${params.row.id}`, responseType: 'blob' })
                        .then(response => {
                            const disposition = response.headers['content-disposition'] ?? '';
                            const match = disposition.match(/filename[^;=\n]*=['"]?([^'"\n;]+)['"]?/i);
                            const fileName = match ? match[1] : `${params.row.template_name}_${params.row.id}.pdf`;
                            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', fileName);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                        })
                        .catch(() => setDisplayAlert('error'));
                };

                const onSend = (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Inviare il documento "${params.row.template_name}" a ${params.row.recipient_phone || 'destinatario'}?`)) return;
                    Api.post({ endpoint: `document/${params.row.id}/send`, contentType: 'application/json', body: {} })
                        .then(response => {
                            const updated = response.data.sent_pdf;
                            setRows(prev => prev.map(r => r.id === updated.id ? { ...r, status: updated.status, sent_at: updated.sent_at } : r));
                        })
                        .catch(() => setDisplayAlert('error'));
                };

                return <>
                    <ActionButton title="Scarica DOCX" onClick={onDownload}>
                        <DownloadIcon />
                    </ActionButton>
                    {params.row.status === 'word_generated' && (
                        <ActionButton title="Carica PDF" onClick={e => { e.stopPropagation(); openUpload(params.row.id); }}>
                            <UploadFileIcon />
                        </ActionButton>
                    )}
                    {(params.row.status === 'pdf_uploaded' || params.row.status === 'documento_inviato') && (
                        <ActionButton title="Scarica PDF" onClick={onDownloadPdf}>
                            <PictureAsPdfIcon />
                        </ActionButton>
                    )}
                    {params.row.status === 'pdf_uploaded' && (
                        <ActionButton title="Invia documento al cliente" onClick={onSend}>
                            <SendIcon />
                        </ActionButton>
                    )}
                    <ActionButton title="Elimina" onClick={onDelete} disabled={params.row.status === 'documento_inviato'}>
                        <DeleteIcon />
                    </ActionButton>
                </>;
            },
        },
    ];

    const theme = useTheme();
    return (
        <>
            <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <h5 style={{ margin: 0 }}>Lista documenti inviati</h5>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormOpen(true)}
                >
                    Invia nuovo documento
                </Button>
            </Box>

            <Box sx={{ width: '100%', minWidth: '800px' }}>
                <ThemeProvider theme={theme}>
                    <DataGrid
                        sx={{ minHeight: '300px' }}
                        rows={rows}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: PAGE_SIZE } },
                        }}
                        apiRef={apiRef}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            columnsPanel: { disableHideAllButton: true },
                            toolbar: { showQuickFilter: true },
                        }}
                        pageSizeOptions={pageSizeOptions}
                    />
                </ThemeProvider>
                {displayAlert === 'error' && (
                    <Alert severity="error" onClose={() => setDisplayAlert(null)}>
                        Si è verificato un errore imprevisto. Riprovare più tardi.
                    </Alert>
                )}
                {displayAlert === 'uploaded' && (
                    <Alert severity="success" onClose={() => setDisplayAlert(null)}>
                        PDF caricato correttamente.
                    </Alert>
                )}
            </Box>

            <CustomerPdfForm
                customerId={customerId}
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSuccess={handleFormSuccess}
            />

            {/* Dialog upload PDF */}
            <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Carica PDF firmato</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {uploadError && <Alert severity="error">{uploadError}</Alert>}
                        <Button variant="outlined" component="label">
                            Seleziona file PDF
                            <input type="file" accept="application/pdf" hidden onChange={handleFileChange} />
                        </Button>
                        {uploadFile && <span>{uploadFile.name}</span>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialogOpen(false)} disabled={uploadLoading}>Annulla</Button>
                    <Button variant="contained" onClick={handleUploadSubmit} disabled={uploadLoading || !uploadFile}>
                        {uploadLoading ? 'Caricamento…' : 'Carica'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default CustomerPdfOverview;
